import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, Input, Select } from '../../../components/ui/Field.jsx'
import { useFeedback } from '../../../components/ui/feedback.jsx'
import { useCountries } from '../../../lib/useCountries.js'
import api from '../../../lib/api.js'

const REQUIRED = ['contact_first_name', 'contact_last_name', 'address', 'postal_code', 'city', 'country', 'email', 'phone']

/* Step 1 — company contact + address (required). PUT /companies/:id. */
export default function StepCompany({ companyId, bindSubmit }) {
  const { t } = useTranslation()
  const { toast } = useFeedback()
  const countries = useCountries()
  const [f, setF] = useState(null)
  const [showErrors, setShowErrors] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  // Highlight a required field once the user has tried to continue with it empty.
  const err = (k) => (showErrors && !String((f && f[k]) || '').trim() ? t('common.required') : undefined)

  useEffect(() => {
    // Prefill from the company; fall back to the registering user's profile for
    // the contact name/email so step 1 isn't empty on a fresh company.
    Promise.all([
      api.get(`/companies/${companyId}`),
      api.get('/auth/profile').catch(() => null),
    ]).then(([co, prof]) => {
      const c = { ...(co.data?.company || co.data) }
      const u = prof?.data?.user || prof?.data || {}
      if (!c.contact_first_name && u.first_name) c.contact_first_name = u.first_name
      if (!c.contact_last_name && u.last_name) c.contact_last_name = u.last_name
      if (!c.email && u.email) c.email = u.email
      // The country Select shows 'BE' by default — make sure state carries it too,
      // otherwise the required-field check fails on an apparently-filled field.
      if (!c.country) c.country = 'BE'
      setF(c)
    })
  }, [companyId])

  useEffect(() => {
    bindSubmit(async () => {
      if (!f) return false
      if (REQUIRED.some((k) => !String(f[k] || '').trim())) {
        // Don't advance silently — show which fields are missing.
        setShowErrors(true)
        toast(t('setup.steps.company.fillRequired'), 'error')
        return false
      }
      try { await api.put(`/companies/${companyId}`, f); return true }
      catch (e) { toast(e?.response?.data?.error || t('settings.errorGeneric'), 'error'); return false }
    })
  }, [f, companyId, bindSubmit, t, toast])

  if (!f) return <div style={{ color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
  return (
    <>
      <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 18 }}>{t('setup.steps.company.intro')}</p>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <RO label={t('admin.companies.name')} value={f.name} />
        <RO label={t('parties.fields.vatNumber')} value={f.vat_number} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label={t('settings.profile.firstName')} required error={err('contact_first_name')}><Input value={f.contact_first_name || ''} onChange={(e) => set('contact_first_name', e.target.value)} /></Field>
        <Field label={t('settings.profile.lastName')} required error={err('contact_last_name')}><Input value={f.contact_last_name || ''} onChange={(e) => set('contact_last_name', e.target.value)} /></Field>
        <Field label="Email" required error={err('email')}><Input type="email" value={f.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label={t('parties.fields.phone')} required error={err('phone')}><Input value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} /></Field>
      </div>
      <Field label={t('parties.fields.address')} required error={err('address')}><Input value={f.address || ''} onChange={(e) => set('address', e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 14 }}>
        <Field label={t('parties.fields.postalCode')} required error={err('postal_code')}><Input value={f.postal_code || ''} onChange={(e) => set('postal_code', e.target.value)} /></Field>
        <Field label={t('parties.fields.city')} required error={err('city')}><Input value={f.city || ''} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field label={t('parties.fields.country')} required>
          <Select value={f.country || 'BE'} onChange={(e) => set('country', e.target.value)}>{countries.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}</Select>
        </Field>
      </div>
    </>
  )
}

function RO({ label, value }) {
  return (
    <div style={{ flex: 1, background: 'var(--color-bg-lilac)', borderRadius: 10, padding: '8px 12px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--color-ink)', fontWeight: 600 }}>{value || '—'}</div>
    </div>
  )
}
